import { ArrowUp, StopCircle } from 'lucide-react'
import {
	forwardRef,
	useEffect,
	useState,
	type TextareaHTMLAttributes,
} from 'react'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import { cn } from '#app/utils/misc.tsx'

export interface TextareaChatProps
	extends TextareaHTMLAttributes<HTMLTextAreaElement> {
	isValid: boolean
	textareaRef: React.RefObject<HTMLTextAreaElement>
	eventSourceRef: any
}

const TextareaChat = forwardRef<HTMLTextAreaElement, TextareaChatProps>(
	({ className, isValid, textareaRef, eventSourceRef, ...props }, ref) => {
		const dispatch = useGlobalChatStore(state => state.dispatch)
		const isCurrentlyStreaming = useGlobalChatStore(
			state => state.isCurrentlyStreaming,
		)
		const [isOverflowing, setIsOverflowing] = useState(false)

		useEffect(() => {
			const handleResize = () => {
				if (textareaRef.current) {
					textareaRef.current.style.height = 'auto'
					textareaRef.current.style.height = `${Math.min(
						textareaRef.current.scrollHeight,
						20 * 20,
					)}px`
					setIsOverflowing(textareaRef.current.scrollHeight > 400)
				}
			}

			handleResize()

			const currentRef = textareaRef.current
			if (currentRef) {
				currentRef.addEventListener('input', handleResize)
				currentRef.addEventListener('focus', handleResize)
			}

			return () => {
				if (currentRef) {
					currentRef.removeEventListener('input', handleResize)
					currentRef.removeEventListener('focus', handleResize)
				}
			}
		}, [textareaRef, isCurrentlyStreaming, isOverflowing, isValid])

		return (
			<div className="relative ">
				<textarea
					rows={1}
					className={cn(
						`bg-white-100 flex border
					   border-input py-5 pl-3 pr-16 text-sm shadow-lg file:border-0 
						file:bg-transparent file:text-sm file:font-medium 
						placeholder:text-muted-foreground focus:outline-none focus:ring-0
						disabled:cursor-not-allowed disabled:opacity-50
						aria-[invalid]:border-input-invalid`,
						className,
						isOverflowing ? 'overflow-y-scroll' : 'overflow-y-hidden',
					)}
					style={{
						height: 'auto',
						maxHeight: '400px',
						minHeight: '65px',
						resize: 'none',
						zIndex: 50,
					}}
					ref={textareaRef}
					{...props}
				/>
				{isCurrentlyStreaming ? (
					<button
						type="button"
						className={`absolute bottom-[1.1rem] right-6 flex transform items-center justify-center rounded-lg`}
						onClick={e => {
							e.preventDefault()
							if (eventSourceRef.current) {
								eventSourceRef.current.close()
								eventSourceRef.current = null
								dispatch({
									type: 'SET_IS_CURRENTLY_STREAMING',
									isCurrentlyStreaming: false,
								})
							}
						}}
					>
						<StopCircle className="h-8  w-8 text-primary-400" />
					</button>
				) : (
					<button
						type="submit"
						disabled={!isValid}
						className={`absolute bottom-[1rem] right-6 flex h-8 w-8 transform items-center justify-center rounded-lg ${
							isValid ? 'bg-primary-400' : 'bg-gray-500'
						}`}
					>
						<ArrowUp className="text-white" />
					</button>
				)}
			</div>
		)
	},
)
TextareaChat.displayName = 'TextareaChat'

export { TextareaChat }
